"use client";
import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Save, Plus, Trash2, Loader2, MessageSquare } from "lucide-react";
import { getCompanyName } from "@/lib/textron-data-processor";
import { DocumentManager } from "./DocumentManager";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

interface AbcotdMaterialityData {
  abcotdRequired: string;
  abcotdDetails: Array<{
    fsSubLine: string;
    abcotdBalance: number;
    percentageApplied: number;
    abcotdMaterialityBasedOnPercent: number;
    determinedMateriality: number;
    determinedPM: number;
  }>;
}

interface AbcotdMaterialityProps {
  onDataChange: (data: AbcotdMaterialityData) => void;
  initialData: AbcotdMaterialityData | null;
  crossSectionAnswers?: Record<string, string | number | boolean>;
  companyName?: string;
}

export function AbcotdMateriality({
  onDataChange,
  initialData,
  crossSectionAnswers = {},
  companyName: propCompanyName,
}: AbcotdMaterialityProps) {
  const { isArchivalMode } = useArchivalMode();
  const [companyName, setCompanyName] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);
  
  // Document states for M-11
  const [m11Documents, setM11Documents] = useState<any[]>([]);
  const [m11TextResponse, setM11TextResponse] = useState<any>(null);

  const [formData, setFormData] = useState({
    abcotdRequired: "",
    abcotdDetails: [] as Array<{
      fsSubLine: string;
      abcotdBalance: number;
      percentageApplied: number;
      abcotdMaterialityBasedOnPercent: number;
      determinedMateriality: number;
      determinedPM: number;
    }>,
  });

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      if (propCompanyName) {
        setCompanyName(propCompanyName);
        return;
      }
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, [propCompanyName]);

  // Get unique FS SubLines for dropdown from FinData_TrialBalance_Mapping.json in juggernaut folder
  // The file structure: { "SEZ": [{ fs_sub_line: "...", ... }, ...], "Non SEZ": [...], ... }
  // We need to extract all unique fs_sub_line values across all books
  const [fsSubLines, setFsSubLines] = useState<string[]>([]);

  useEffect(() => {
    const loadFsSubLines = async () => {
      try {
        if (!(window as any).sharePointAPI) {
          console.warn("SharePoint API not available");
          setFsSubLines([]);
          return;
        }

        // Fetch mapping data from SharePoint (FinData_TrialBalance_Mapping.json in juggernaut folder)
        const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping?.({ includeAmounts: true });
        if (!mappingResult?.success || !mappingResult?.data) {
          console.warn("Failed to load mapping data from SharePoint");
          setFsSubLines([]);
          return;
        }

        const mappingData = mappingResult.data;
        const uniqueSubLines = new Set<string>();

        // Iterate through all books (SEZ, Non SEZ, etc.) to get all unique fs_sub_line values
        for (const [bookName, entries] of Object.entries(mappingData)) {
          if (Array.isArray(entries)) {
            entries.forEach((entry: any) => {
              const fsSubLine = entry.fs_sub_line || entry.fsSubLine || "";
              // Include all non-empty fs_sub_line values (excluding "Other" as it's not a valid FS Sub-line)
              if (fsSubLine && fsSubLine.trim() !== "" && fsSubLine !== "Other") {
                uniqueSubLines.add(fsSubLine);
              }
            });
          }
        }

        // Sort alphabetically for better UX
        setFsSubLines(Array.from(uniqueSubLines).sort());
      } catch (error) {
        console.error("Error loading FS SubLines:", error);
        setFsSubLines([]);
      }
    };

    loadFsSubLines();
  }, []);

  // Function to get ABCOTD balance for a specific FS SubLine from FinData_TrialBalance_Mapping.json
  // The balance is the sum of all amounts for the selected FS Sub-line for the latest date
  // Example: If latest date is "31/03/25", sum all amount["31/03/25"] values for entries with matching fs_sub_line
  const getFsSubLineBalance = useCallback(async (fsSubLine: string): Promise<number> => {
    if (!fsSubLine) return 0;

    try {
      if (!(window as any).sharePointAPI) {
        console.warn("SharePoint API not available");
        return 0;
      }

      // Fetch mapping data from SharePoint (FinData_TrialBalance_Mapping.json in juggernaut folder)
      const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping?.({ includeAmounts: true });
      if (!mappingResult?.success || !mappingResult?.data) {
        console.warn("Failed to load mapping data from SharePoint");
        return 0;
      }

      const mappingData = mappingResult.data;

      // Step 1: Collect all dates from amount fields across all books to find the latest date
      const allDates: string[] = [];
      for (const [bookName, entries] of Object.entries(mappingData)) {
        if (Array.isArray(entries)) {
          entries.forEach((entry: any) => {
            if (entry.amount && typeof entry.amount === 'object') {
              Object.keys(entry.amount).forEach(date => {
                if (!allDates.includes(date)) {
                  allDates.push(date);
                }
              });
            }
          });
        }
      }

      // Step 2: Parse dates and find the latest date (e.g., "31/03/25" = March 31, 2025)
      const parseDate = (dateStr: string): Date => {
        const [day, month, year] = dateStr.split('/');
        // Convert 2-digit year to 4-digit (25 -> 2025)
        return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
      };

      const sortedDates = allDates.sort((a, b) => {
        const dateA = parseDate(a);
        const dateB = parseDate(b);
        return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
      });

      const latestDate = sortedDates[0];
      if (!latestDate) {
        console.warn("No dates found in mapping data");
        return 0;
      }

      console.log(`[ABCOTD] Using latest date: ${latestDate} for FS Sub-line: ${fsSubLine}`);

      // Step 3: Sum all amounts for the latest date for all entries matching the fs_sub_line across all books
      let totalBalance = 0;
      for (const [bookName, entries] of Object.entries(mappingData)) {
        if (Array.isArray(entries)) {
          entries.forEach((entry: any) => {
            const entryFsSubLine = entry.fs_sub_line || entry.fsSubLine || "";
            // Match the selected FS Sub-line
            if (entryFsSubLine === fsSubLine && entry.amount) {
              const amountData = entry.amount;
              // Get amount for the latest date
              if (typeof amountData === 'object' && amountData[latestDate] !== undefined) {
                const amount = parseFloat(amountData[latestDate]) || 0;
                totalBalance += amount;
              }
            }
          });
        }
      }

      // Return absolute value of the balance
      const absoluteBalance = Math.abs(totalBalance);
      console.log(`[ABCOTD] Total balance for ${fsSubLine}: ${absoluteBalance} (from ${latestDate}, original: ${totalBalance})`);
      return absoluteBalance;
    } catch (error) {
      console.error("Error calculating FS SubLine balance:", error);
      return 0;
    }
  }, []);

  // Load data from SharePoint when component mounts
  useEffect(() => {
    const loadFromSharePoint = async () => {
      if (!companyName) return;

      try {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          console.log("Planning API not available, using initialData");
          if (initialData) {
            const loadInitialData = async () => {
              const details = await Promise.all(
                (initialData.abcotdDetails || []).map(async (detail) => {
                  let balance = detail.abcotdBalance || 0;
                  if (detail.fsSubLine) {
                    balance = await getFsSubLineBalance(detail.fsSubLine);
                  }
                  // Ensure balance is absolute
                  const absoluteBalance = Math.abs(balance);
                  const percentage = detail.percentageApplied || 0;
                  // Recalculate materiality: percentage × balance / 100
                  const materiality = percentage > 0 ? (absoluteBalance * percentage) / 100 : 0;
                  return {
                    fsSubLine: detail.fsSubLine || "",
                    abcotdBalance: absoluteBalance,
                    percentageApplied: percentage,
                    abcotdMaterialityBasedOnPercent: materiality,
                    determinedMateriality: detail.determinedMateriality || 0,
                    determinedPM: detail.determinedPM || 0,
                  };
                })
              );
              setFormData({
                abcotdRequired: initialData.abcotdRequired || "",
                abcotdDetails: details,
              });
            };
            loadInitialData();
          }
          return;
        }

        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "materiality"
        });

        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;
          console.log("Loaded ABCOTD responses from SharePoint:", responses);

          const loadedData: AbcotdMaterialityData = {
            abcotdRequired: "",
            abcotdDetails: []
          };

          // Load M-10 (ABCOTD Required)
          if (responses['M-10'] && responses['M-10'].selectedOption) {
            loadedData.abcotdRequired = responses['M-10'].selectedOption;
          }

          // Load M-11 (Documentation)
          if (responses['M-11']) {
            const m11Response = responses['M-11'];
            if (m11Response.textResponse) {
              setM11TextResponse(m11Response.textResponse);
            }
            if (m11Response.documents) {
              setM11Documents(m11Response.documents);
            }
          }

          // Load ABCOTD Table
          if (responses['abcotdTable'] && responses['abcotdTable'].columns) {
            const tableColumns = responses['abcotdTable'].columns;
            const details = await Promise.all(
              tableColumns.map(async (col: any) => {
                const values = col.values || {};
                let balance = values.balance || 0;
                // Recalculate balance from Trial Balance if FS SubLine is present
                if (values.abcotd) {
                  balance = await getFsSubLineBalance(values.abcotd);
                }
                // Ensure balance is absolute
                const absoluteBalance = Math.abs(balance);
                const percentage = values.percentApplied || 0;
                // Recalculate materiality: percentage × balance / 100
                const materiality = percentage > 0 ? (absoluteBalance * percentage) / 100 : 0;
                return {
                  fsSubLine: values.abcotd || "",
                  abcotdBalance: absoluteBalance,
                  percentageApplied: percentage,
                  abcotdMaterialityBasedOnPercent: materiality,
                  determinedMateriality: values.determinedMateriality || 0,
                  determinedPM: values.determinedPM || 0,
                };
              })
            );
            loadedData.abcotdDetails = details;
          }

          // If no SharePoint data, fallback to initialData
          if (!loadedData.abcotdRequired && (!loadedData.abcotdDetails || loadedData.abcotdDetails.length === 0)) {
            if (initialData) {
              const loadInitialData = async () => {
                const details = await Promise.all(
                  (initialData.abcotdDetails || []).map(async (detail) => {
                    let balance = detail.abcotdBalance || 0;
                    if (detail.fsSubLine) {
                      balance = await getFsSubLineBalance(detail.fsSubLine);
                    }
                    return {
                      fsSubLine: detail.fsSubLine || "",
                      abcotdBalance: balance,
                      percentageApplied: detail.percentageApplied || 0,
                      abcotdMaterialityBasedOnPercent:
                        detail.abcotdMaterialityBasedOnPercent || 0,
                      determinedMateriality: detail.determinedMateriality || 0,
                      determinedPM: detail.determinedPM || 0,
                    };
                  })
                );
                setFormData({
                  abcotdRequired: initialData.abcotdRequired || "",
                  abcotdDetails: details,
                });
              };
              loadInitialData();
              return;
            }
          }

          setFormData(loadedData);
        } else {
          // Fallback to initialData if SharePoint loading fails
          if (initialData) {
            const loadInitialData = async () => {
              const details = await Promise.all(
                (initialData.abcotdDetails || []).map(async (detail) => {
                  let balance = detail.abcotdBalance || 0;
                  if (detail.fsSubLine) {
                    balance = await getFsSubLineBalance(detail.fsSubLine);
                  }
                  // Ensure balance is absolute
                  const absoluteBalance = Math.abs(balance);
                  const percentage = detail.percentageApplied || 0;
                  // Recalculate materiality: percentage × balance / 100
                  const materiality = percentage > 0 ? (absoluteBalance * percentage) / 100 : 0;
                  return {
                    fsSubLine: detail.fsSubLine || "",
                    abcotdBalance: absoluteBalance,
                    percentageApplied: percentage,
                    abcotdMaterialityBasedOnPercent: materiality,
                    determinedMateriality: detail.determinedMateriality || 0,
                    determinedPM: detail.determinedPM || 0,
                  };
                })
              );
              setFormData({
                abcotdRequired: initialData.abcotdRequired || "",
                abcotdDetails: details,
              });
            };
            loadInitialData();
          }
        }
      } catch (error) {
        console.error("Failed to load ABCOTD responses from SharePoint:", error);
        // Fallback to initialData
        if (initialData) {
          const loadInitialData = async () => {
            const details = await Promise.all(
              (initialData.abcotdDetails || []).map(async (detail) => {
                let balance = detail.abcotdBalance || 0;
                if (detail.fsSubLine) {
                  balance = await getFsSubLineBalance(detail.fsSubLine);
                }
                return {
                  fsSubLine: detail.fsSubLine || "",
                  abcotdBalance: balance,
                  percentageApplied: detail.percentageApplied || 0,
                  abcotdMaterialityBasedOnPercent:
                    detail.abcotdMaterialityBasedOnPercent || 0,
                  determinedMateriality: detail.determinedMateriality || 0,
                  determinedPM: detail.determinedPM || 0,
                };
              })
            );
            setFormData({
              abcotdRequired: initialData.abcotdRequired || "",
              abcotdDetails: details,
            });
          };
          loadInitialData();
        }
      }
    };

    loadFromSharePoint();
  }, [companyName, initialData, getFsSubLineBalance]);

  const addAbcotdDetail = () => {
    setFormData((prev) => ({
      ...prev,
      abcotdDetails: [
        ...prev.abcotdDetails,
        {
          fsSubLine: "",
          abcotdBalance: 0,
          percentageApplied: 0,
          abcotdMaterialityBasedOnPercent: 0,
          determinedMateriality: 0,
          determinedPM: 0,
        },
      ],
    }));
  };

  const updateAbcotdDetail = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    setFormData((prev) => {
      const newDetails = [...prev.abcotdDetails];
      newDetails[index] = { ...newDetails[index], [field]: value };

      // Auto-calculate fields based on changes
      if (field === "fsSubLine") {
        // Update ABCOTD Balance when FS SubLine changes (async)
        getFsSubLineBalance(value as string).then((balance) => {
          setFormData((prev) => {
            const updatedDetails = [...prev.abcotdDetails];
            if (updatedDetails[index]) {
              // Store absolute balance
              updatedDetails[index].abcotdBalance = Math.abs(balance);
              // Recalculate materiality: percentage × balance
              // If user enters 5 (meaning 5%), materiality = 5 × balance / 100
              const percentage = updatedDetails[index].percentageApplied || 0;
              if (percentage > 0) {
                const absoluteBalance = Math.abs(balance);
                updatedDetails[index].abcotdMaterialityBasedOnPercent =
                  (absoluteBalance * percentage) / 100;
              }
            }
            return { ...prev, abcotdDetails: updatedDetails };
          });
        });
        // Set temporary balance to 0 while loading
        newDetails[index].abcotdBalance = 0;
      } else if (field === "percentageApplied") {
        // Recalculate materiality when percentage changes
        // Use absolute balance for calculation
        const balance = Math.abs(newDetails[index].abcotdBalance || 0);
        const percentage = Number(value) || 0;
        if (balance > 0 && percentage > 0) {
          // Materiality = percentage × balance / 100
          // If user enters 5 (meaning 5%), materiality = 5 × balance / 100
          newDetails[index].abcotdMaterialityBasedOnPercent =
            (balance * percentage) / 100;
        } else {
          newDetails[index].abcotdMaterialityBasedOnPercent = 0;
        }
      }

      return { ...prev, abcotdDetails: newDetails };
    });
  };

  const removeAbcotdDetail = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      abcotdDetails: prev.abcotdDetails.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    // Call the original onDataChange for local state management
    onDataChange(formData);
    
    // Save to SharePoint
    if (!companyName) {
      setSaveError("Company name not available");
      setSaveStatus('error');
      return;
    }

    // Trigger auto-save for DocumentManager before saving
    try {
    } catch (error) {
      console.warn('Failed to trigger auto-save for DocumentManager:', error);
    }

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const responses: Record<string, any> = {};

      // M-10: ABCOTD Required (radio question)
      if (formData.abcotdRequired) {
        responses['M-10'] = {
          questionId: 'M-10',
          selectedOption: formData.abcotdRequired,
          textResponse: null,
          documents: [],
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // M-11: Documentation question (document type)
      const hasM11Documents = m11Documents && Array.isArray(m11Documents) && m11Documents.length > 0;
      const hasM11TextResponse = m11TextResponse && (
        (m11TextResponse.html && m11TextResponse.html.trim().length > 0) || 
        (m11TextResponse.plainText && m11TextResponse.plainText.trim().length > 0)
      );
      
      if (hasM11Documents || hasM11TextResponse) {
        const fileDocuments = hasM11Documents 
          ? m11Documents.filter((doc: any) => doc.type !== 'textbox').map((doc: any) => ({
              name: doc.name,
              type: doc.type,
              url: doc.url,
              reference: doc.reference
            }))
          : [];
        
        const m11TextResponseData = hasM11TextResponse ? m11TextResponse : null;
        
        responses['M-11'] = {
          questionId: 'M-11',
          selectedOption: null,
          textResponse: m11TextResponseData,
          documents: fileDocuments,
          lastUpdated: new Date().toISOString(),
          companyName
        };
      }

      // ABCOTD Table - save in the specified format
      if (formData.abcotdDetails && formData.abcotdDetails.length > 0) {
        responses['abcotdTable'] = {
          rows: [
            { key: "abcotd", label: "ABCOTD" },
            { key: "balance", label: "ABCOTD Balance" },
            { key: "percentApplied", label: "% Applied" },
            { key: "materialityPct", label: "ABCOTD Materiality based on %" },
            { key: "determinedMateriality", label: "Determined Materiality" },
            { key: "determinedPM", label: "Determined PM" }
          ],
          columns: formData.abcotdDetails.map((detail, index) => ({
            id: `abcotd${index + 1}`,
            label: `ABCOTD ${index + 1}`,
            values: {
              abcotd: detail.fsSubLine || null,
              balance: detail.abcotdBalance || 0,
              percentApplied: detail.percentageApplied || 0,
              materialityPct: detail.abcotdMaterialityBasedOnPercent || 0,
              determinedMateriality: detail.determinedMateriality || 0,
              determinedPM: detail.determinedPM || 0
            }
          }))
        };
      }

      // Save all responses to SharePoint
      if (Object.keys(responses).length > 0) {
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI) {
          throw new Error("Planning API not available");
        }

        const result = await planningAPI.saveAllResponses({
          companyName,
          sectionKey: "materiality",
          responses
        });

        if (result.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(result.error || "Failed to save responses");
        }
      } else {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error) {
      console.error("Failed to save ABCOTD responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Debug log for tracking cross-section answers (for future conditional logic)
  console.log("AbcotdMateriality crossSectionAnswers:", crossSectionAnswers);

  return (
    <div className="space-y-6">
      {/* Header with Review Comment Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">ABCOTD Materiality</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setReviewCommentOpen(true)}
          className="text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
          title="Review Comment"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Review Comment
        </Button>
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="abcotdRequired">
            Do we need to determine materiality for particular classes of
            transactions, account balances or disclosures?
          </Label>
          <RadioGroup
            value={formData.abcotdRequired}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, abcotdRequired: value }))
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Yes" id="abcotd-yes" />
              <Label htmlFor="abcotd-yes">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="No" id="abcotd-no" />
              <Label htmlFor="abcotd-no">No</Label>
            </div>
          </RadioGroup>
        </div>

        {formData.abcotdRequired === "Yes" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">ABCOTD Details</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addAbcotdDetail}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add ABCOTD Entry
              </Button>
            </div>

            {/* ABCOTD Table */}
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="border-r p-3 text-left text-sm font-medium">
                      Field
                    </th>
                    {(formData.abcotdDetails || []).map((_, index) => (
                      <th
                        key={index}
                        className="border-r p-3 text-center text-sm font-medium"
                      >
                        ABCOTD {index + 1}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAbcotdDetail(index)}
                          className="ml-2 h-6 w-6 p-0 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* ABCOTD Row */}
                  <tr className="border-b">
                    <td className="border-r p-3 text-sm font-medium">ABCOTD</td>
                    {(formData.abcotdDetails || []).map((detail, index) => (
                      <td key={index} className="border-r p-3">
                        <select
                          value={detail.fsSubLine}
                          onChange={(e) =>
                            updateAbcotdDetail(
                              index,
                              "fsSubLine",
                              e.target.value,
                            )
                          }
                          className="w-full rounded border border-gray-300 p-2 text-sm"
                        >
                          <option value="">Select FS SubLine</option>
                          {fsSubLines.map((subLine) => (
                            <option key={subLine} value={subLine}>
                              {subLine}
                            </option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>

                  {/* ABCOTD Balance Row */}
                  <tr className="border-b">
                    <td className="border-r p-3 text-sm font-medium">
                      ABCOTD Balance
                    </td>
                    {(formData.abcotdDetails || []).map((detail, index) => (
                      <td key={index} className="border-r p-3">
                        <div className="rounded p-2 text-center text-sm">
                          {(detail.abcotdBalance || 0).toLocaleString('en-IN')}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          (Auto-computed from FS SubLine)
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* % Applied Row */}
                  <tr className="border-b">
                    <td className="border-r p-3 text-sm font-medium">
                      % Applied
                    </td>
                    {(formData.abcotdDetails || []).map((detail, index) => (
                      <td key={index} className="border-r p-3">
                        <Input
                          type="number"
                          value={detail.percentageApplied || ""}
                          onChange={(e) =>
                            updateAbcotdDetail(
                              index,
                              "percentageApplied",
                              Number.parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0"
                          min="0"
                          max="100"
                          step="0.1"
                          className="text-center"
                        />
                      </td>
                    ))}
                  </tr>

                  {/* ABCOTD Materiality based on % Row */}
                  <tr className="border-b">
                    <td className="border-r p-3 text-sm font-medium">
                      ABCOTD Materiality based on %
                    </td>
                    {(formData.abcotdDetails || []).map((detail, index) => (
                      <td key={index} className="border-r p-3">
                        <div className="rounded p-2 text-center text-sm font-medium">
                          {(
                            detail.abcotdMaterialityBasedOnPercent || 0
                          ).toLocaleString('en-IN')}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          (Auto-computed)
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* Determined Materiality Row */}
                  <tr className="border-b">
                    <td className="border-r p-3 text-sm font-medium">
                      Determined Materiality
                    </td>
                    {(formData.abcotdDetails || []).map((detail, index) => (
                      <td key={index} className="border-r p-3">
                        <Input
                          type="number"
                          value={detail.determinedMateriality || ""}
                          onChange={(e) =>
                            updateAbcotdDetail(
                              index,
                              "determinedMateriality",
                              Number.parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0"
                          className="text-center"
                        />
                      </td>
                    ))}
                  </tr>

                  {/* Determined PM Row */}
                  <tr>
                    <td className="border-r p-3 text-sm font-medium">
                      Determined PM
                    </td>
                    {(formData.abcotdDetails || []).map((detail, index) => (
                      <td key={index} className="border-r p-3">
                        <Input
                          type="number"
                          value={detail.determinedPM || ""}
                          onChange={(e) =>
                            updateAbcotdDetail(
                              index,
                              "determinedPM",
                              Number.parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0"
                          className="text-center"
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* M-11: Documentation Question */}
            <div className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label>
                  Document the reason(s) for setting a lower level materiality and performance materiality for particular ABCOTDs, including the factors considered in their determination.
                </Label>
                <DocumentManager
                  sectionKey="materiality"
                  groupKey="ABCOTD Materiality"
                  itemId="M-11"
                  itemQuestion="Document the reason(s) for setting a lower level materiality and performance materiality for particular ABCOTDs, including the factors considered in their determination."
                  itemTextResponse={m11TextResponse}
                  itemDocuments={m11Documents}
                  onDocumentsChange={setM11Documents}
                  onTextResponseChange={setM11TextResponse}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end items-center gap-4 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600">
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && saveError && (
          <Badge variant="destructive" className="text-red-600">
            Error: {saveError}
          </Badge>
        )}
        <Button 
          onClick={handleSave} 
          className="flex items-center gap-2"
          disabled={isArchivalMode || saving || !companyName}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save ABCOTD Data
            </>
          )}
        </Button>
      </div>
      
      {/* Review Comment Modal */}
      <ReviewComment
        questionId="Materiality-ABCOTD"
        questionText="ABCOTD Materiality"
        isOpen={reviewCommentOpen}
        onClose={() => setReviewCommentOpen(false)}
        workspace="Materiality"
        subPhase="ABCOTD Materiality"
      />
    </div>
  );
}
